import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Brand } from '../utils/brandModelStorage';
import { checkForDuplicate } from '../utils/fuzzyMatch';
import { DidYouMeanDialog } from './DidYouMeanDialog';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';

interface CreateItemModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  title: string;
  placeholder: string;
  label: string;
  suggestedValue?: string;
  // Optional: For fuzzy matching (brands only)
  existingBrands?: Brand[];
  enableFuzzyMatching?: boolean;
  onSelectExisting?: (brand: Brand) => void;
}

/**
 * Reusable modal for creating brands, families, or models
 * Includes validation, auto-formatting, and loading states
 */
export function CreateItemModal({
  visible,
  onClose,
  onSubmit,
  title,
  placeholder,
  label,
  suggestedValue,
  existingBrands = [],
  enableFuzzyMatching = false,
  onSelectExisting,
}: CreateItemModalProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        header: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
        },
        title: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
        },
        label: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.sm,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        },
        previewContainer: {
          marginTop: theme.spacing.md,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
        },
        previewLabel: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginBottom: theme.spacing.xxs,
          letterSpacing: 0,
        },
        previewText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        buttonContainer: {
          flexDirection: 'row',
          gap: theme.spacing.md,
          marginTop: theme.spacing.xl,
        },
        button: {
          flex: 1,
        },
      }),
    [theme],
  );
  const [inputValue, setInputValue] = useState(suggestedValue || '');
  const [creating, setCreating] = useState(false);
  const [showDidYouMean, setShowDidYouMean] = useState(false);
  const [suggestedBrand, setSuggestedBrand] = useState<(Brand & { similarity: number; matchType: 'exact' | 'alias' | 'fuzzy' }) | null>(null);

  // Reset input when modal opens with new suggested value
  React.useEffect(() => {
    if (visible && suggestedValue) {
      setInputValue(suggestedValue);
    }
  }, [visible, suggestedValue]);

  const formatName = (name: string): string => {
    // Trim whitespace
    let formatted = name.trim();

    // Capitalize first letter of each word
    formatted = formatted
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return formatted;
  };

  const handleSubmit = async () => {
    const trimmed = inputValue.trim();

    if (!trimmed) {
      Alert.alert('Error', `Please enter a ${label.toLowerCase()}`);
      return;
    }

    if (trimmed.length < 2) {
      Alert.alert('Error', `${label} must be at least 2 characters`);
      return;
    }

    if (trimmed.length > 100) {
      Alert.alert('Error', `${label} must be less than 100 characters`);
      return;
    }

    // Check for fuzzy matches (brands only)
    if (enableFuzzyMatching && existingBrands.length > 0) {
      const duplicate = checkForDuplicate(trimmed, existingBrands);

      if (duplicate) {
        setSuggestedBrand(duplicate);
        setShowDidYouMean(true);
        return; // Don't proceed with creation
      }
    }

    // Proceed with creation
    await performCreate();
  };

  const performCreate = async () => {
    const trimmed = inputValue.trim();
    setCreating(true);
    try {
      const formatted = formatName(trimmed);
      await onSubmit(formatted);
      setInputValue('');
      onClose();
    } catch (error: any) {
      console.error(`Error creating ${label.toLowerCase()}:`, error);

      // Check for duplicate error
      if (error.message?.includes('duplicate') || error.code === '23505') {
        Alert.alert('Already Exists', `A ${label.toLowerCase()} with this name already exists.`);
      } else {
        Alert.alert('Error', `Failed to create ${label.toLowerCase()}. Please try again.`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleUseExisting = () => {
    setShowDidYouMean(false);
    if (suggestedBrand && onSelectExisting) {
      onSelectExisting(suggestedBrand);
    }
    setInputValue('');
    onClose();
  };

  const handleCreateNew = async () => {
    setShowDidYouMean(false);
    await performCreate();
  };

  const handleCancelDidYouMean = () => {
    setShowDidYouMean(false);
    setSuggestedBrand(null);
  };

  const handleClose = () => {
    if (!creating) {
      setInputValue('');
      onClose();
    }
  };

  const previewFormatted = inputValue.trim() ? formatName(inputValue) : '';

  return (
    <>
      <Modal
        animationType="fade"
        transparent={true}
        visible={visible && !showDidYouMean}
        onRequestClose={handleClose}
      >
        <View style={commonStyles.modalOverlay}>
          <View style={commonStyles.modalContent}>
            <View style={styles.header}>
              <Text style={styles.title}>{title}</Text>
              <TouchableOpacity onPress={handleClose} disabled={creating}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>{label}</Text>
            <TextInput
              style={commonStyles.input}
              value={inputValue}
              onChangeText={setInputValue}
              placeholder={placeholder}
              placeholderTextColor={theme.colors.textTertiary}
              autoCapitalize="words"
              autoCorrect={false}
              editable={!creating}
              onSubmitEditing={handleSubmit}
              returnKeyType="done"
            />

            {previewFormatted && previewFormatted !== inputValue && (
              <View style={styles.previewContainer}>
                <Text style={styles.previewLabel}>Will be saved as</Text>
                <Text style={styles.previewText}>{previewFormatted}</Text>
              </View>
            )}

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[commonStyles.secondaryButton, styles.button]}
                onPress={handleClose}
                disabled={creating}
              >
                <Text style={commonStyles.secondaryButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  commonStyles.primaryButton,
                  styles.button,
                  // Empty input → unavailable; creating → busy (opacity).
                  !inputValue.trim() && commonStyles.primaryButtonDisabled,
                  creating && commonStyles.disabledButton,
                ]}
                onPress={handleSubmit}
                disabled={creating || !inputValue.trim()}
                accessibilityState={{ disabled: creating || !inputValue.trim(), busy: creating }}
                testID="create-item-submit"
              >
                {creating ? (
                  <ActivityIndicator size="small" color={theme.colors.onAccent} />
                ) : (
                  <Text
                    style={[
                      commonStyles.primaryButtonText,
                      !inputValue.trim() && commonStyles.disabledButtonText,
                    ]}
                  >
                    Create
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Did You Mean Dialog */}
      <DidYouMeanDialog
        visible={showDidYouMean}
        userInput={inputValue}
        suggestedBrand={suggestedBrand}
        onUseExisting={handleUseExisting}
        onCreateNew={handleCreateNew}
        onCancel={handleCancelDidYouMean}
      />
    </>
  );
}

